import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiError, apiClient } from "../../shared/api/client";
import {
  DOC_TYPES,
  PAPER_SIZES,
  printTemplatesApi,
  printTemplatesQueryKeys,
  type DocTypeKey,
  type PaperSize,
  type PrintDocTemplate,
  type PrintTemplatesPayload,
} from "../../shared/api/printTemplates";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * T86 — Mẫu in.
 *
 * Lite scope: edit paper size + header / subtitle / footer / signature label for the three Lite
 * document types (đơn thuốc, hoá đơn, biên lai). Right-hand pane shows a live A5/A4 preview that
 * mirrors what `PrescriptionPrintPage` / `InvoiceReceiptPage` render at print time.
 */
export function PrintTemplatesSettingsPage() {
  const qc = useQueryClient();
  const [activeDoc, setActiveDoc] = useState<DocTypeKey>("prescription");
  const [draft, setDraft] = useState<PrintTemplatesPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pull the clinic header (name/address/phone) so the preview reflects what the patient sees.
  const clinic = useQuery({
    queryKey: ["clinic-settings"],
    queryFn: () =>
      apiClient.get<ClinicHeader>("/v1/clinic/settings").then((r) => r.data),
  });

  const query = useQuery({
    queryKey: printTemplatesQueryKeys.all(),
    queryFn: () => printTemplatesApi.get(),
  });

  // hydrate draft once the GET resolves
  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data);
  }, [query.data, draft]);

  const save = useMutation({
    mutationFn: () => printTemplatesApi.update(draft!),
    onSuccess: (saved) => {
      qc.setQueryData(printTemplatesQueryKeys.all(), saved);
      setDraft(saved);
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  function patchDoc(patch: Partial<PrintDocTemplate>) {
    if (!draft) return;
    setDraft({ ...draft, [activeDoc]: { ...draft[activeDoc], ...patch } });
  }

  function resetDoc() {
    if (!query.data || !draft) return;
    setDraft({ ...draft, [activeDoc]: query.data[activeDoc] });
  }

  const docTemplate = draft?.[activeDoc];
  const dirty = useMemo(
    () =>
      draft != null &&
      query.data != null &&
      JSON.stringify(draft) !== JSON.stringify(query.data),
    [draft, query.data]
  );

  return (
    <div className="font-sans">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Mẫu in
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tuỳ chỉnh mẫu in đơn thuốc, hoá đơn, biên lai cho đúng yêu cầu
            phòng khám — khổ giấy, tiêu đề, chân trang và chỗ ký. Bản xem
            thử bên phải khớp với bản in thật.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={resetDoc}
            disabled={!dirty}
          >
            Hủy thay đổi
          </Button>
          <Button
            type="button"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={save.isPending || !dirty}
          >
            {save.isPending ? "Đang lưu…" : "Lưu mẫu in"}
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
        {DOC_TYPES.map((d) => {
          const isActive = d.key === activeDoc;
          return (
            <Button
              key={d.key}
              type="button"
              variant={isActive ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveDoc(d.key)}
              className="rounded-b-none"
              aria-pressed={isActive}
            >
              {d.label}
            </Button>
          );
        })}
      </div>

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải mẫu in…</p>
      )}

      {docTemplate && (
        <div className="grid gap-6 lg:grid-cols-[minmax(320px,420px)_1fr]">
          <Editor template={docTemplate} onChange={patchDoc} />
          <Preview template={docTemplate} clinic={clinic.data ?? null} />
        </div>
      )}
    </div>
  );
}

// ====================================================================== Editor

function Editor({
  template,
  onChange,
}: {
  template: PrintDocTemplate;
  onChange: (patch: Partial<PrintDocTemplate>) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tuỳ chỉnh</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="paper-size">Khổ giấy</Label>
          <Select
            value={template.paperSize}
            onValueChange={(v) => onChange({ paperSize: v as PaperSize })}
          >
            <SelectTrigger id="paper-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAPER_SIZES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="show-clinic-header"
            checked={template.showClinicHeader}
            onCheckedChange={(checked) =>
              onChange({ showClinicHeader: checked === true })
            }
          />
          <Label htmlFor="show-clinic-header" className="font-normal">
            Hiển thị đầu trang phòng khám (tên, địa chỉ, điện thoại)
          </Label>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="header-title">Tiêu đề chính</Label>
          <Input
            id="header-title"
            type="text"
            value={template.headerTitle}
            onChange={(e) => onChange({ headerTitle: e.target.value })}
            maxLength={64}
            placeholder="VD: ĐƠN THUỐC"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="header-subtitle">Phụ đề (tuỳ chọn)</Label>
          <Input
            id="header-subtitle"
            type="text"
            value={template.headerSubtitle}
            onChange={(e) => onChange({ headerSubtitle: e.target.value })}
            maxLength={128}
            placeholder="VD: Số ……  Ngày …… tháng …… năm ……"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signature-label">Nhãn dưới chỗ ký</Label>
          <Input
            id="signature-label"
            type="text"
            value={template.signatureLabel}
            onChange={(e) => onChange({ signatureLabel: e.target.value })}
            maxLength={64}
            placeholder="VD: Bác sĩ ký tên"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="footer-text">Chân trang</Label>
          <Textarea
            id="footer-text"
            rows={2}
            value={template.footerText}
            onChange={(e) => onChange({ footerText: e.target.value })}
            maxLength={200}
            placeholder="VD: Cảm ơn quý khách. Hẹn gặp lại!"
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ===================================================================== Preview

interface ClinicHeader {
  name: string;
  address: string | null;
  phone: string | null;
}

function Preview({
  template,
  clinic,
}: {
  template: PrintDocTemplate;
  clinic: ClinicHeader | null;
}) {
  // Visual page dimensions in CSS pixels. Real print honours @page from each print page;
  // here we use a relative ratio that "feels" right next to a real A5/A4 print.
  const isA4 = template.paperSize === "A4";

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        Xem trước · Khổ {template.paperSize}
      </div>
      <div
        aria-label="print-preview"
        className={
          "mx-auto max-w-full bg-white text-[12px] leading-normal text-neutral-900 shadow-lg " +
          (isA4
            ? "w-[420px] min-h-[594px] p-6"
            : "w-[330px] min-h-[467px] p-4")
        }
        style={{ color: "#111" }}
      >
        {template.showClinicHeader && (
          <div className="mb-2">
            <strong>{clinic?.name ?? "(Tên phòng khám)"}</strong>
            <div className="text-[11px]">
              {clinic?.address ?? "(Địa chỉ phòng khám)"}
            </div>
            {clinic?.phone && (
              <div className="text-[11px]">ĐT: {clinic.phone}</div>
            )}
          </div>
        )}

        <h2 className="mb-1 mt-2 text-center text-[16px]">
          {template.headerTitle || "(Tiêu đề)"}
        </h2>
        {template.headerSubtitle && (
          <div className="mb-2 text-center text-[11px]">
            {template.headerSubtitle}
          </div>
        )}

        <div className="mt-3 text-[11px]">
          <strong>Bệnh nhân:</strong> Nguyễn Văn A (BN-0001) — ĐT:
          0901 234 567
          <br />
          <strong>Ngày:</strong> 16/05/2026
        </div>

        <table className="mt-2 w-full border-collapse text-[11px]">
          <thead>
            <tr>
              <th className="w-6 border border-neutral-500 p-[3px] text-left">
                #
              </th>
              <th className="border border-neutral-500 p-[3px] text-left">
                Nội dung
              </th>
              <th className="w-[60px] border border-neutral-500 p-[3px] text-right">
                SL
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-neutral-500 p-[3px]">1</td>
              <td className="border border-neutral-500 p-[3px]">
                Dòng minh hoạ 1
              </td>
              <td className="border border-neutral-500 p-[3px] text-right">
                10
              </td>
            </tr>
            <tr>
              <td className="border border-neutral-500 p-[3px]">2</td>
              <td className="border border-neutral-500 p-[3px]">
                Dòng minh hoạ 2
              </td>
              <td className="border border-neutral-500 p-[3px] text-right">
                5
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6 flex justify-end text-[11px]">
          <div className="min-w-[160px] text-center">
            <div>Ngày …… tháng …… năm ……</div>
            <div className="mt-1">
              <strong>{template.signatureLabel || "(Chỗ ký)"}</strong>
            </div>
            <div className="mt-9">……………………</div>
          </div>
        </div>

        {template.footerText && (
          <div className="mt-4 border-t border-dashed border-neutral-400 pt-1.5 text-center text-[10px] italic">
            {template.footerText}
          </div>
        )}
      </div>
    </div>
  );
}
